import {
  envelope,
  isEnvelope,
  type BridgeObservation,
  type HostMessage,
  type IframeMessage,
} from '../bridge/protocol';
import type {CanvasBridgeAdapter, EntityDispatchResult} from './adapter';

/**
 * Host-side counterpart to bridge/client.ts. Routes postMessage events
 * between a single iframe window and a CanvasBridgeAdapter. Constructed
 * once per mounted `<CanvasIframe>`, destroyed on unmount.
 *
 * Two `window.addEventListener('message', ...)` exist — one in this file,
 * one in bridge/client.ts — because the router runs in the host's window
 * (listening for messages the iframe posts up), while the client runs
 * inside the iframe's null-origin window (listening for messages the host
 * posts down). Different realms, different listeners.
 *
 * Package-internal — the host app interacts with the runtime through
 * `<CanvasIframe adapter={...} />`, never with the router directly.
 */
export class CanvasBridgeRouter {
  readonly dispose: () => void;
  readonly observe: (
    fn: (observation: BridgeObservation) => void,
  ) => () => void;
  /**
   * Ask the iframe to serialize its rendered DOM into a printable document.
   * Host→iframe request/response (mirrors the iframe→host `entity.dispatch`
   * shape, with the roles flipped). Rejects on timeout or dispose.
   */
  readonly requestSnapshot: () => Promise<{html: string; width: number}>;
  /** Toggle hover-to-edit text mode inside the runtime. */
  readonly setTextEditMode: (enabled: boolean) => void;
  /** Commit the active text-edit session and wait until its report arrives. */
  readonly commitTextEdits: () => Promise<void>;
  /** Restore every text edit the runtime is holding to its rendered original. */
  readonly resetTextEdits: () => void;

  constructor(
    iframeWindow: Window,
    adapter: CanvasBridgeAdapter,
    onSize?: (height: number) => void,
  ) {
    let disposed = false;
    const cleanups: Array<() => void> = [];
    const observers = new Set<(observation: BridgeObservation) => void>();

    const SNAPSHOT_TIMEOUT_MS = 15_000;
    const TEXT_EDIT_COMMIT_TIMEOUT_MS = 5_000;
    let nextSnapshotId = 1;
    let nextTextEditCommitId = 1;
    const pendingSnapshots = new Map<
      string,
      {
        resolve: (value: {html: string; width: number}) => void;
        reject: (err: Error) => void;
        timeout: ReturnType<typeof setTimeout>;
      }
    >();
    const pendingTextEditCommits = new Map<
      string,
      {
        resolve: () => void;
        reject: (err: Error) => void;
        timeout: ReturnType<typeof setTimeout>;
      }
    >();

    const emit = (
      direction: 'in' | 'out',
      message: IframeMessage | HostMessage,
    ): void => {
      if (observers.size === 0) {
        return;
      }
      const observation: BridgeObservation = {
        direction,
        message,
        occurredAt: new Date().toISOString(),
      };
      observers.forEach((fn) => {
        fn(observation);
      });
    };

    // `runtime.rendered` and `runtime.size` arrive in either order; gate
    // `adapter.onRendered` on having seen both so consumers get a single
    // "paint-ready" signal.
    let hasFirstSize = false;
    let pendingRender: {occurredAt: string; source: string} | null = null;
    // The iframe script may not have installed its listener yet when the
    // host first toggles text mode, so the last request is replayed once
    // the runtime announces itself.
    let textEditModeEnabled = false;

    const send = (message: HostMessage): void => {
      if (disposed) {
        return;
      }
      // targetOrigin '*' — we always run in a null-origin sandboxed iframe,
      // which means the iframe doesn't know its parent's origin and the
      // parent can't restrict by origin string. Receivers on the iframe
      // side ignore non-canvas-runtime envelopes; receivers on the host
      // side check `event.source === iframeWindow`.
      iframeWindow.postMessage(envelope(message), '*');
      emit('out', message);
    };

    const route = async (request: IframeMessage): Promise<void> => {
      switch (request.kind) {
        case 'bootstrap.request': {
          send({kind: 'bootstrap', payload: adapter.getBootstrap()});
          if (textEditModeEnabled) {
            send({kind: 'textedit.mode', enabled: true});
          }
          return;
        }
        case 'entity.dispatch': {
          // Convert adapter throws into a `{ok: false, error}` response so
          // the iframe's dispatch promise resolves cleanly instead of
          // hanging until the bridge request timeout fires. Adapters should
          // prefer returning `{ok: false, error}` for expected failures;
          // throws here represent unexpected bugs but still need a defined
          // reply.
          let result: EntityDispatchResult;
          try {
            result = await adapter.dispatchEntityAction(
              request.id,
              request.action,
              request.payload,
            );
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            adapter.onRuntimeError?.(
              message,
              err instanceof Error ? err.stack : undefined,
            );
            result = {ok: false, error: message};
          }
          if (disposed) {
            return;
          }
          send({
            kind: 'entity.dispatch.result',
            requestId: request.requestId,
            ...result,
          });
          return;
        }
        case 'snapshot.result': {
          const pending = pendingSnapshots.get(request.requestId);
          if (pending) {
            clearTimeout(pending.timeout);
            pendingSnapshots.delete(request.requestId);
            pending.resolve({html: request.html, width: request.width});
          }
          return;
        }
        case 'textedit.commit.result': {
          const pending = pendingTextEditCommits.get(request.requestId);
          if (pending) {
            clearTimeout(pending.timeout);
            pendingTextEditCommits.delete(request.requestId);
            pending.resolve();
          }
          return;
        }
        case 'runtime.error': {
          adapter.onRuntimeError?.(request.message, request.stack);
          return;
        }
        case 'runtime.rendered': {
          if (hasFirstSize) {
            adapter.onRendered?.(request.occurredAt, request.source);
          } else {
            pendingRender = {
              occurredAt: request.occurredAt,
              source: request.source,
            };
          }
          return;
        }
        case 'runtime.size': {
          onSize?.(request.height);
          if (!hasFirstSize) {
            hasFirstSize = true;
            if (pendingRender) {
              adapter.onRendered?.(
                pendingRender.occurredAt,
                pendingRender.source,
              );
              pendingRender = null;
            }
          }
          return;
        }
      }
    };

    const handleMessage = (event: MessageEvent): void => {
      // Only accept messages from our iframe — rejects messages from other
      // frames on the page that happen to use the same protocol tag.
      if (event.source !== iframeWindow) {
        return;
      }
      if (!isEnvelope(event.data)) {
        return;
      }
      const incoming = event.data.message as IframeMessage;
      emit('in', incoming);
      // route() can reject (e.g., adapter.getBootstrap throws). Without
      // explicit handling the rejection is unhandled and the iframe spins
      // forever waiting for a bootstrap response. Surface it through
      // onRuntimeError so the host can react.
      route(incoming).catch((err: unknown) => {
        if (disposed) {
          return;
        }
        const message = err instanceof Error ? err.message : String(err);
        const stack = err instanceof Error ? err.stack : undefined;
        adapter.onRuntimeError?.(message, stack);
      });
    };

    window.addEventListener('message', handleMessage);
    cleanups.push(() => {
      window.removeEventListener('message', handleMessage);
    });

    if (adapter.subscribeSourceUpdates) {
      const unsubscribe = adapter.subscribeSourceUpdates(
        (source, annotatedSource) => {
          send({kind: 'source.update', source, annotatedSource});
        },
      );
      cleanups.push(unsubscribe);
    }

    if (adapter.subscribeEntityUpdates) {
      const unsubscribe = adapter.subscribeEntityUpdates(() => {
        let payload;
        try {
          payload = adapter.getBootstrap();
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          const stack = err instanceof Error ? err.stack : undefined;
          adapter.onRuntimeError?.(message, stack);
          return;
        }
        send({kind: 'bootstrap', payload});
      });
      cleanups.push(unsubscribe);
    }

    if (adapter.subscribeBrandUpdates) {
      const unsubscribe = adapter.subscribeBrandUpdates((brand) => {
        send({kind: 'brand.update', brand});
      });
      cleanups.push(unsubscribe);
    }

    this.requestSnapshot = () => {
      if (disposed) {
        return Promise.reject(new Error('canvas bridge disposed'));
      }
      const requestId = `snap_${nextSnapshotId++}`;

      return new Promise<{html: string; width: number}>((resolve, reject) => {
        const timeout = setTimeout(() => {
          pendingSnapshots.delete(requestId);
          reject(new Error('snapshot request timed out'));
        }, SNAPSHOT_TIMEOUT_MS);

        pendingSnapshots.set(requestId, {resolve, reject, timeout});
        send({kind: 'snapshot.request', requestId});
      });
    };

    this.setTextEditMode = (enabled) => {
      textEditModeEnabled = enabled;
      send({kind: 'textedit.mode', enabled});
    };

    this.commitTextEdits = () => {
      if (disposed) {
        return Promise.reject(new Error('canvas bridge disposed'));
      }
      const requestId = `textedit_commit_${nextTextEditCommitId++}`;
      return new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          pendingTextEditCommits.delete(requestId);
          reject(new Error('text edit commit timed out'));
        }, TEXT_EDIT_COMMIT_TIMEOUT_MS);

        pendingTextEditCommits.set(requestId, {resolve, reject, timeout});
        send({kind: 'textedit.commit', requestId});
      });
    };

    this.resetTextEdits = () => {
      send({kind: 'textedit.reset'});
    };

    this.dispose = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      cleanups.forEach((fn) => {
        fn();
      });
      pendingSnapshots.forEach((pending) => {
        clearTimeout(pending.timeout);
        pending.reject(new Error('canvas bridge disposed'));
      });
      pendingSnapshots.clear();
      pendingTextEditCommits.forEach((pending) => {
        clearTimeout(pending.timeout);
        pending.reject(new Error('canvas bridge disposed'));
      });
      pendingTextEditCommits.clear();
      observers.clear();
    };

    this.observe = (fn) => {
      observers.add(fn);
      return () => {
        observers.delete(fn);
      };
    };
  }
}
